import type { Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { computed, unref } from 'vue'
import { getApi } from './api'

export interface FindContainersParams {
  container_type_id: number | null
  destination_geofence_id: number | null
  radius_m?: number | null
}

export type FindContainersSiteGeometry =
  | { type: 'circle'; lat: number; lng: number; radius_m: number; points: null }
  | { type: 'polygon'; lat: number; lng: number; radius_m: null; points: [number, number][] }

export interface FindContainersSite {
  geofence_id: number
  name: string
  address: string | null
  min_assets: number | null
  max_assets: number | null
  units: number
  first_entered_at: string
  distance_m: number | null
  geometry: FindContainersSiteGeometry | null
}

export interface FindContainersTracker {
  device_id: number
  name: string
  lat: number
  lng: number
  timestamp: string
  fix: string | null
  last_movement_time: string | null
  updated_time: string | null
  address: string | null
  battery: number | null
  temperature: number | null
  humidity: number | null
  geofence_ids: number[]
}

export interface FindContainersResult {
  sites: FindContainersSite[]
  trackers: FindContainersTracker[]
}

/**
 * Sites holding trackers of one container type, nearest to the destination first.
 * Answers 404 while `returnable_containers_available` is off, like the container-types
 * cluster; the URL is whitelisted in the customer app's interceptor for the same reason.
 */
export default (
  accountId: Ref<number> | number,
  params: Ref<FindContainersParams>,
) => {
  return useQuery({
    queryKey: ['account-find-containers', () => unref(accountId), params],
    queryFn: async (): Promise<FindContainersResult | null> => {
      const { api, baseURL } = getApi()
      const { radius_m, ...required } = unref(params)
      const res = await api.get(
        `/api/v2/accounts/${unref(accountId)}/find-containers`,
        {
          baseURL,
          params: radius_m == null ? required : { ...required, radius_m },
          allow404: true,
        },
      )
      return res?.data?.data ?? null
    },
    enabled: computed(
      () =>
        !!unref(accountId) &&
        !!unref(params).container_type_id &&
        !!unref(params).destination_geofence_id,
    ),
  })
}
